import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import * as CordovaSQLiteDriver from 'localforage-cordovasqlitedriver';
import * as CryptoJS from 'crypto-js';
import { from, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly storagePrefix = '__srph_secure_v1__:';
  private readonly storageSecret = 'saferoadsph-storage-v1';
  private _storage: Storage | null = null;
  private initPromise: Promise<void> | null = null;
  private browserStoragePatched = false;
  private browserStorageOriginals: {
    setItem: (key: string, value: string) => void;
    getItem: (key: string) => string | null;
    removeItem: (key: string) => void;
    clear: () => void;
  } | null = null;

  constructor(private storage: Storage) {
    this.patchBrowserStorage();
  }

  async init() {
    if (this._storage) {
      return;
    }

    if (!this.initPromise) {
      this.initPromise = (async () => {
        // If using, define drivers here: await this.storage.defineDriver(/*...*/);
        await this.storage.defineDriver(CordovaSQLiteDriver);
        const storage = await this.storage.create();
        this._storage = storage;
      })();
    }

    await this.initPromise;
  }

  private patchBrowserStorage() {
    if (this.browserStoragePatched || typeof localStorage === 'undefined') {
      return;
    }

    const browserStorage = localStorage;
    this.browserStorageOriginals = {
      setItem: browserStorage.setItem.bind(browserStorage),
      getItem: browserStorage.getItem.bind(browserStorage),
      removeItem: browserStorage.removeItem.bind(browserStorage),
      clear: browserStorage.clear.bind(browserStorage),
    };

    browserStorage.setItem = (key: string, value: string) => {
      this.browserStorageOriginals?.setItem(key, this.encryptValue(value));
    };

    browserStorage.getItem = (key: string) => {
      const storedValue = this.browserStorageOriginals?.getItem(key);
      return this.decryptValue<string>(storedValue);
    };

    browserStorage.removeItem = (key: string) => {
      this.browserStorageOriginals?.removeItem(key);
    };

    browserStorage.clear = () => {
      this.browserStorageOriginals?.clear();
    };

    this.browserStoragePatched = true;
  }

  private async ensureReady(): Promise<Storage> {
    await this.init();
    return this._storage as Storage;
  }

  private encryptValue(value: any): string {
    const serializedValue = JSON.stringify(value ?? null);
    const cipherText = CryptoJS.AES.encrypt(serializedValue, this.storageSecret).toString();
    return `${this.storagePrefix}${cipherText}`;
  }

  private decryptValue<T>(value: string | null | undefined): T | string | null {
    if (value === null || value === undefined || !value.startsWith(this.storagePrefix)) {
      return value ?? null;
    }

    try {
      const cipherText = value.slice(this.storagePrefix.length);
      const bytes = CryptoJS.AES.decrypt(cipherText, this.storageSecret);
      const plainText = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(plainText) as T;
    } catch {
      return null;
    }
  }

  public async set(key: string, value: any) {
    const storage = await this.ensureReady();
    return storage.set(key, this.encryptValue(value));
  }

  async get(key: any): Promise<any> {
    const storage = await this.ensureReady();
    const value = await storage.get(key);
    return this.decryptValue(value);
  }

  async clear() {
    const storage = await this.ensureReady();
    await storage.clear();
    if (typeof localStorage !== 'undefined') {
      localStorage.clear();
    }
  }

  async remove(key: any) {
    const storage = await this.ensureReady();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
    return storage.remove(key);
  }

  async clearAll() {
    await this.clear();
  }

  observableStoragetGet(key: any) {
    return from(
      this.get(key).then((res) => {
        return res;
      })
    );
  }
}
